import { ActionIcon, Divider, Flex, Text } from '@mantine/core';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { MyIcon } from '@web/common-ui';
import { reschedule } from '../book-in-person/cal-dot-com.links';
import type { NachversandAppointment } from './nachversandBannerState';

const BRAND_GREEN = '#3D7F53';
const iconStyle = { width: 22, height: 22, flexShrink: 0 };
const linkStyle = { color: BRAND_GREEN, fontWeight: 500, fontSize: 16 };

// Same layout as the shared InfoItem, but at the banner's body size. InfoItem hardcodes
// 14px and is also used by the onboarding appointment card, so it is left alone.
const DetailRow = ({
  icon,
  children,
}: {
  icon: string;
  children: React.ReactNode;
}) => (
  <Flex gap="14px" align="flex-start">
    <MyIcon name={icon} style={{ ...iconStyle, marginTop: 2 }} />
    <Flex direction="column">{children}</Flex>
  </Flex>
);

const detailTextProps = { size: '16px', lh: '1.5', c: '#202020' } as const;

/**
 * The booked video call, as the expanded content of the Nachversand banner. No location or
 * map: it is a video call, so there is nothing to show. "Bereithalten: Studienausweis + Pass"
 * is deliberately not repeated here — the Prepare paragraph below already says it.
 */
export const NachversandAppointmentDetails = ({
  appointment,
  withTrailingDivider = true,
}: {
  // also the Part 2 "Versand" tab, whose endpoint carries only these fields
  appointment: Pick<
    NachversandAppointment,
    'id' | 'startTime' | 'endTime' | 'location'
  >;
  // separates the details from whatever follows — off when they are the last thing in the banner
  withTrailingDivider?: boolean;
}) => {
  const { t, i18n } = useTranslation('waiting-screen');
  const startTime = DateTime.fromJSDate(appointment.startTime);
  const endTime = DateTime.fromJSDate(appointment.endTime);
  // The Nachversand endpoint fills `location` with the cal.com join URL derived from the booking
  // uid, always — so for a booked call this check is effectively always true. It stays as the guard
  // that keeps the link out of the markup if the endpoint ever hands back the '' the booking
  // webhook wrote.
  const joinUrl = appointment.location.startsWith('http')
    ? appointment.location
    : null;

  const formattedDate = startTime
    .setLocale(i18n.language)
    .toFormat('EEEE, d. MMMM yyyy');
  const formattedTimeRange = `${startTime.toFormat('HH:mm')} - ${endTime.toFormat('HH:mm')}`;
  const durationInMinutes = endTime.diff(startTime, 'minutes').minutes;

  return (
    <>
      <Flex direction="column" gap="10px">
        <DetailRow icon="calendar">
          <Flex align="center" gap="8px">
            <Text {...detailTextProps}>{formattedDate}</Text>
            <ActionIcon
              component="a"
              href={reschedule(appointment.id)}
              target="_self"
              rel="noreferrer"
              variant="subtle"
              size="sm"
              aria-label={t('Nachversand.Booked.Reschedule')}
              title={t('Nachversand.Booked.Reschedule')}
            >
              <MyIcon name="edit" style={{ fill: BRAND_GREEN }} />
            </ActionIcon>
          </Flex>
          <Text {...detailTextProps}>{formattedTimeRange}</Text>
        </DetailRow>
        <DetailRow icon="clock">
          <Text {...detailTextProps}>
            {t('Nachversand.Booked.Duration', { minutes: durationInMinutes })}
          </Text>
        </DetailRow>
        <DetailRow icon="videocam">
          <Flex gap="10px" align="center" wrap="wrap">
            <Text {...detailTextProps}>
              {t('Nachversand.Booked.VideoCall')}
            </Text>
            {joinUrl && (
              <a
                href={joinUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={linkStyle}
              >
                {t('Nachversand.Booked.JoinCall')}
              </a>
            )}
          </Flex>
        </DetailRow>
      </Flex>

      {withTrailingDivider && <Divider mt="16px" mb="16px" />}
    </>
  );
};
